import React, { useState } from "react";
import Image from "next/image";

import defaultImage from "../../../../public/images/image20.jpeg";
import bgImg from "../../../../public/images/t19bg.png";
import { GetWeddingPartyQuery } from "@src/@types/api.d";
import { WeddingPartyType } from "@src/components/CustomTemplate2/WeddingParty";

export type IWeddingParty = {
  eventId: number;
  weddingPartyData: GetWeddingPartyQuery;
  loading?: boolean;
  sectionImage?: string;
};

interface PersonCardProps {
  person: WeddingPartyType;
}

const WeddingParty: React.FC<IWeddingParty> = ({
  weddingPartyData,
  sectionImage,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const weddingPartyMembers = (weddingPartyData?.getWeddingParty || []).filter(
    (member): member is WeddingPartyType =>
      member !== null &&
      !!member.first_name &&
      !!member.last_name &&
      !!member.role
  );

  const sortedMembers = [...weddingPartyMembers].sort((a, b) => {
    if (a?.order_index !== null && b?.order_index !== null) {
      return a?.order_index! - b?.order_index!;
    }
    if (a?.order_index !== null && b?.order_index === null) return -1;
    if (a?.order_index === null && b?.order_index !== null) return 1;
    return 0;
  });

  if (!sortedMembers.length) return null;

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % sortedMembers.length);
  };

  const handlePrevious = () => {
    setCurrentIndex(
      (prev) => (prev - 1 + sortedMembers.length) % sortedMembers.length
    );
  };

  const PersonCard: React.FC<PersonCardProps> = ({ person }) => (
    <div className="flex flex-col items-center text-center text-[#FCE2B0] max-w-md mx-auto">
      <div className="w-48 h-64 sm:w-56 sm:h-72 lg:w-64 lg:h-80 mb-6 rounded-lg overflow-hidden bg-gray-300 relative">
        {person?.image_url ? (
          <Image
            src={person.image_url}
            alt={`${person.first_name} ${person.last_name}`}
            layout="fill"
            className="object-cover"
            sizes="(max-width: 768px) 192px, 256px"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#8C7756] to-amber-600">
            <span className="text-2xl md:text-3xl lg:text-5xl font-semibold text-black uppercase">
              {person?.first_name.charAt(0)}
              {person?.last_name.charAt(0)}
            </span>
          </div>
        )}
      </div>
      <h3 className="text-xl sm:text-2xl lg:text-3xl font-ntFab tracking-wide uppercase mb-2">
        {person?.first_name} {person?.nickname ? `"${person.nickname}"` : ""}{" "}
        {person?.last_name}
      </h3>
      <p className="text-base sm:text-lg lg:text-xl font-ebGaramond italic text-[#FFF3DB]">
        {person?.role}
      </p>
    </div>
  );

  const prevIndex =
    (currentIndex - 1 + sortedMembers.length) % sortedMembers.length;
  const nextIndex = (currentIndex + 1) % sortedMembers.length;
  return (
    <div className="w-full flex flex-col lg:flex-row lg:h-[100vh]">
      {/* Content Section - Takes natural height on mobile, centered on desktop */}
      <div className="w-full lg:flex-1 lg:h-full relative order-1 lg:order-2">
        {/* Background image */}
        <div className="absolute inset-0">
          <Image
            src={bgImg}
            alt="background"
            layout="fill"
            className="object-cover object-center"
          />
        </div>

        <div className="relative z-10 w-full h-full flex flex-col justify-center items-center py-12 lg:py-20 px-6 lg:px-10">
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-ntFab text-[#FCE2B0] mb-4">
            The Wedding Party
          </h1>
          <p className="text-base sm:text-lg lg:text-xl font-ebGaramond text-[#FCE2B0] max-w-2xl mx-auto text-center mb-8 lg:mb-12">
            Meet the amazing people who will celebrate this special day with us.
          </p>

          {/* Person Card Display */}
          <div className="flex-1 flex items-center justify-center mb-8">
            <PersonCard person={sortedMembers[currentIndex]} />
          </div>

          {/* Navigation Controls */}
          <div className="flex items-center justify-center gap-4 lg:gap-8">
            <button
              onClick={() => setCurrentIndex(prevIndex)}
              className="w-10 h-10 lg:w-14 lg:h-14 border border-[#FCE2B0] text-[#FCE2B0] hover:bg-[#FCE2B0] hover:text-[#00234B] transition-all duration-300 rounded-full flex items-center justify-center"
            >
              <svg
                className="w-5 h-5 lg:w-6 lg:h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>

            <button
              onClick={() => setCurrentIndex(nextIndex)}
              className="w-10 h-10 lg:w-14 lg:h-14 border border-[#FCE2B0] text-[#FCE2B0] hover:bg-[#FCE2B0] hover:text-[#00234B] transition-all duration-300 rounded-full flex items-center justify-center"
            >
              <svg
                className="w-5 h-5 lg:w-6 lg:h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Image Section - Fixed height on mobile, follows content */}
      <div className="w-full h-[50vh] lg:flex-1 lg:h-full order-2 lg:order-1 lg:hidden">
        <div className="relative w-full h-full">
          <Image
            loading="eager"
            layout="fill"
            src={sectionImage || defaultImage}
            alt="wedding-party-image"
            className="object-cover object-center lg:object-top"
            sizes="(max-width: 1024px) 100vw, 50vw"
          />
        </div>
      </div>
    </div>
  );
};

export default WeddingParty;
